/**
 * LegalShieldModal — Zéro Relais UI
 *
 * Displayed when the Coach YUMIX (or a manual trigger) detects signs of
 * psychological / medical distress. The modal:
 *   1. Surfaces geo-localized emergency resources (API → fallback).
 *   2. Forces the user to tick a legal disclaimer checkbox.
 *   3. Logs the acknowledgement through /api/safety/redirect (audit).
 *
 * This component is the single source of truth for the legal shield.
 * Accepts either an already-resolved `resources` payload (from SSE)
 * or a `country` + `language` for the component to fetch itself.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Phone, ExternalLink, ShieldCheck, X } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';

const PUBLIC_BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LegalShieldModal({
  open,
  onClose,
  onAcknowledge,
  resources: resourcesProp,       // Optional — if parent already has them
  country: countryProp,           // Optional — used if we need to fetch
  language: languageProp = 'fr',
  trigger = 'manual_shield',
  topics = [],
  authToken = null,               // when called from inside the app
  publicMode = false,             // when called from public test page
}) {
  const [resources, setResources] = useState(resourcesProp || null);
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch resources if not provided
  useEffect(() => {
    if (!open) return;
    if (resourcesProp) {
      setResources(resourcesProp);
      return;
    }
    const fetchResources = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = publicMode
          ? `${PUBLIC_BACKEND}/api/safety/public-resources?country=${encodeURIComponent(countryProp || 'CA')}&language=${encodeURIComponent(languageProp)}`
          : `${PUBLIC_BACKEND}/api/safety/emergency-resources?country=${encodeURIComponent(countryProp || '')}&language=${encodeURIComponent(languageProp)}`;
        const headers = {};
        if (!publicMode && authToken) headers['Authorization'] = `Bearer ${authToken}`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setResources(data);
      } catch (e) {
        setError(e.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, [open, resourcesProp, countryProp, languageProp, publicMode, authToken]);

  const translations = useMemo(() => {
    const isFR = (languageProp || 'fr').startsWith('fr');
    return {
      title: isFR ? 'Bouclier Légal YUMIX' : 'YUMIX Legal Shield',
      subtitle: isFR
        ? 'YUMIX a détecté un signal de détresse. Ta sécurité passe avant tout.'
        : 'YUMIX detected a distress signal. Your safety comes first.',
      resourcesTitle: isFR
        ? `Ressources d'urgence — ${resources?.country_name || ''}`
        : `Emergency resources — ${resources?.country_name || ''}`,
      callAction: isFR ? 'Appeler' : 'Call',
      disclaimer: isFR
        ? "Je confirme avoir pris connaissance des ressources d'urgence ci-dessus. Je comprends que YUMIX n'est pas un professionnel de santé et ne peut pas remplacer une évaluation médicale ou psychologique qualifiée."
        : 'I acknowledge the emergency resources shown above. I understand that YUMIX is not a health professional and cannot replace a qualified medical or psychological evaluation.',
      acknowledge: isFR ? "J'ai compris, reprendre" : 'Got it, continue',
      close: isFR ? 'Fermer' : 'Close',
      loading: isFR ? 'Chargement des ressources…' : 'Loading resources…',
      error: isFR ? 'Impossible de charger les ressources.' : 'Unable to load resources.',
      fallbackBadge: isFR ? 'Numéros officiels pré-validés' : 'Officially curated numbers',
      apiBadge: isFR ? 'Répertoire mondial (ThroughLine)' : 'Global directory (ThroughLine)',
      noticePrefix: isFR ? 'Avis' : 'Notice',
    };
  }, [languageProp, resources]);

  const handleAcknowledge = async () => {
    setSubmitting(true);
    try {
      // Log anonymized audit (only if authenticated — public test page skips)
      if (!publicMode && authToken) {
        await fetch(`${PUBLIC_BACKEND}/api/safety/redirect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            country: resources?.country,
            topics: topics || [],
            trigger,
            consent_accepted: true,
            source: resources?.source || 'fallback',
            language: languageProp,
          }),
        });
      }
      if (onAcknowledge) onAcknowledge();
      if (onClose) onClose();
    } catch (e) {
      console.error('[LegalShield] redirect log failed:', e);
      if (onAcknowledge) onAcknowledge();
      if (onClose) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm p-0 md:p-4"
      data-testid="legal-shield-backdrop"
    >
      <div
        className="w-full md:max-w-lg bg-neutral-900 text-white rounded-t-3xl md:rounded-3xl shadow-2xl border border-red-500/40 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="legal-shield-modal"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-900 p-5 pb-6">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 transition"
            aria-label={translations.close}
            data-testid="legal-shield-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 pr-8">
            <div className="p-2.5 bg-white/15 rounded-xl">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight tracking-tight">
                {translations.title}
              </h2>
              <p className="text-sm text-red-100/90 mt-0.5">
                {translations.subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && (
            <p className="text-neutral-400 text-sm" data-testid="legal-shield-loading">
              {translations.loading}
            </p>
          )}
          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 p-3 rounded-lg">
              {translations.error} ({error})
            </div>
          )}

          {resources && (
            <>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-white tracking-tight">
                  {translations.resourcesTitle}
                </h3>
                <span
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                    resources.source === 'api'
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  }`}
                  data-testid="legal-shield-source-badge"
                >
                  {resources.source === 'api' ? translations.apiBadge : translations.fallbackBadge}
                </span>
              </div>

              {resources.fallback_notice && (
                <p className="text-xs text-amber-200/80 bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 leading-relaxed">
                  <strong>{translations.noticePrefix} :</strong> {resources.fallback_notice}
                </p>
              )}

              <ul className="space-y-2" data-testid="legal-shield-resource-list">
                {resources.resources?.map((r, idx) => {
                  const isUrl = typeof r.number === 'string' && r.number.startsWith('http');
                  const href = isUrl ? r.number : `tel:${(r.number || '').replace(/\s/g, '')}`;
                  return (
                    <li
                      key={`${r.type}-${idx}`}
                      className="flex items-start gap-3 bg-neutral-800/70 hover:bg-neutral-800 rounded-xl p-3 border border-neutral-700/70 transition"
                      data-testid={`legal-shield-resource-${r.type}`}
                    >
                      <div className="p-2 bg-red-500/10 rounded-lg shrink-0">
                        {isUrl ? <ExternalLink className="w-4 h-4 text-red-300" /> : <Phone className="w-4 h-4 text-red-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-white leading-snug">{r.label}</div>
                        <div className="text-lg font-bold tracking-tight text-red-200 mt-0.5">
                          {r.number}
                        </div>
                        {r.available && (
                          <div className="text-[10px] uppercase tracking-wider text-emerald-300 mt-1">
                            {r.available}
                          </div>
                        )}
                      </div>
                      <a
                        href={href}
                        target={isUrl ? '_blank' : undefined}
                        rel={isUrl ? 'noopener noreferrer' : undefined}
                        className="self-center px-3 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-500 active:bg-red-700 rounded-lg transition shrink-0"
                        data-testid={`legal-shield-call-btn-${r.type}`}
                      >
                        {translations.callAction}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* Footer — legal checkbox */}
        <div className="border-t border-neutral-700/70 bg-neutral-950/70 p-5 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => setAccepted(Boolean(v))}
              className="mt-0.5 border-neutral-500 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
              data-testid="legal-shield-consent-checkbox"
            />
            <span className="text-xs text-neutral-300 leading-relaxed">
              {translations.disclaimer}
            </span>
          </label>
          <Button
            onClick={handleAcknowledge}
            disabled={!accepted || submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-neutral-700 disabled:text-neutral-500 text-white font-semibold py-6 rounded-xl transition"
            data-testid="legal-shield-acknowledge-btn"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            {submitting ? '…' : translations.acknowledge}
          </Button>
        </div>
      </div>
    </div>
  );
}
